import Big from 'big.js';
import {QtyType} from '@/const/contract';
import {numUtil} from './num';

const unscale = {
  ep(val, {priceScale: scale} = {priceScale: 0}) {
    return numUtil.unscale(val, scale);
  },
  ev(val, {valueScale: scale} = {valueScale: 0}) {
    return numUtil.unscale(val, scale);
  },
  er(val) {
    return numUtil.unscale(val, 8);
  },
};

const displayFormat = {
  ep(val, {pricePrecision: precision} = {pricePrecision: 8}) {
    return numUtil.floor(val, precision);
  },
  ev(val, {valuePrecision: precision} = {valuePrecision: 8}) {
    return numUtil.floor(val, precision);
  },
  er(val) {
    // 百分比类型的 er，其他的直接使用 unscale 的数据
    return numUtil.floor(val, 2, -2);
  },
};
displayFormat.rp = displayFormat.ep;
displayFormat.rv = displayFormat.ev;
displayFormat.rr = displayFormat.er;

/**
 * @param o: 要转换的对象
 * @param contract: 可以传入 contract 或者 currency
 * @param excludeKeys: 不需要转换的 key
 * @param positionMode: 仓位的精度不按照配置格式化，默认4位
 *
 * 转换对象中的 xxEp, xxEr, xxEv 属性, mutable
 *
 * 转换前：{realizedPnlEv: 123456}
 * 转换后：{
 *  realizedPnlEv: 123456,
 *  realizedPnl: '12.3456',
 *  displayRealizedPnl: '12.34',
 * }
 */
export function autoConvertExData(o, contract, {excludeKeys = [], positionMode = true} = {}) {
  if (!o || !contract) {
    return;
  }

  Object.keys(o).forEach((key) => {
    if (excludeKeys.includes(key)) {
      return;
    }

    const val = o[key];
    const type = key.slice(-2);
    const dataKey = key.slice(0, -2);
    const fePrefix = 'display';
    const displayKey = `${fePrefix}${dataKey.slice(0, 1).toUpperCase()}${dataKey.slice(1)}`;

    if (!isString(val) && !isNumber(val)) {
      return;
    }

    // 对应的是老合约或者后端帮转了的双向合约：老合约要缩放，新合约直接使用转换后的值
    const t = type.toLowerCase();
    if (['Ev', 'Ep', 'Er'].includes(type)) {
      const config = positionMode ? getPositionConfig(contract) : contract;
      if (contract.type === 'PerpetualV2' || o.userId < 0) {
        o[displayKey] = displayFormat[t](o[dataKey], config);
      } else {
        o[dataKey] = unscale[t](val, config);
        o[displayKey] = displayFormat[t](o[dataKey], config);
      }
    }
    // 对应的是后端没做转换，直接给了 Rx 的数据，直接赋值
    if (['Rv', 'Rp', 'Rr'].includes(type)) {
      const config = positionMode ? getPositionConfig(contract) : contract;
      o[dataKey] = val;
      o[displayKey] = displayFormat[t](o[dataKey], config);
    }
  });
}

// 单独转换某个 ex 属性
export function exPropertyCovert(type = 'ev', val, contract, {positionMode = false} = {}) {
  const ex = ['ev', 'ep', 'er'];
  const rx = ['rv', 'rp', 'rr'];
  if (!ex.includes(type) && !rx.includes(type)) {
    throw Error('not valid covert type!');
  }

  if (val === void 0 || !contract) {
    return {
      raw: '',
      scale: '',
      display: '',
    };
  }

  const config = positionMode ? getPositionConfig(contract) : contract;
  let scale;
  let display;
  if (ex.includes(type)) {
    scale = unscale[type](val, config);
    display = displayFormat[type](scale, config);
  }
  if (rx.includes(type)) {
    scale = val;
    display = displayFormat[type](scale, config);
  }

  return {
    raw: type,
    scale,
    display,
  };
}

/**
 * 仅用在 copy-trade 此类数据结构，amount 未标记 amountEv、amountRv
 * {
 *   amount: '123',
 *   currency: 'USDT',
 * }
 */
export function convertAmountData(o) {
  if (!o || !o.amount) {
    return;
  }
  o.displayAmount = numUtil.floor(o.amount, 4);
}

function getPositionConfig(contractConfig) {
  return contractConfig.contractSide === -1 ? {...contractConfig, valuePrecision: 4} : contractConfig;
}

function isString(str) {
  return Object.prototype.toString.call(str) === '[object String]';
}
function isNumber(num) {
  return Object.prototype.toString.call(num) === '[object Number]';
}

/**
 *  委托单位相关的转换
 *  转换数量和单位
 *  在展示合约数量的时候使用
 *
 *  新/老区别：
 *    V2之前、都以 cont 为单位下单
 *    V2之后、都以 contractUnderlyingAssets 为单位下单
 */
export const QtyConvert = {
  toValueUnit(contract) {
    if (!QtyConvert._isV2(contract)) {
      if (contract.contractSide > 0) {
        return contract.settleCurrencyDisplay;
      } else {
        return contract.contractUnderlyingAssets;
      }
    } else {
      return contract.settleCurrencyDisplay;
    }
  },
  toValueQuantity(contract, size, price) {
    if (!QtyConvert._isV2(contract)) {
      if (contract.contractSide > 0) {
        // 多少 usd,  1张 * contractSize(1张多少btc) * price
        if (invalidPrice(price)) {
          return '-';
        }
        return numUtil.floor(Big(size).mul(contract.contractSize).mul(price), 2);
      } else {
        // 多少 usd,  1张 * contractSize(1张多少usd)
        return numUtil.floor(Big(size).mul(contract.contractSize), 2);
      }
    } else {
      if (invalidPrice(price)) {
        return '-';
      }
      // USDT 稳定币
      return numUtil.floor(Big(size).mul(price), 8);
    }
  },
  toValue(contract, size, price) {
    return QtyConvert._display(QtyConvert.toValueQuantity(contract, size, price), QtyConvert.toValueUnit(contract));
  },

  toCoinUnit(contract) {
    if (!QtyConvert._isV2(contract)) {
      if (contract.contractSide > 0) {
        return contract.contractUnderlyingAssets;
      } else {
        return contract.settleCurrencyDisplay;
      }
    } else {
      return contract.contractUnderlyingAssets;
    }
  },
  toCoinQuantity(contract, size, price) {
    if (!QtyConvert._isV2(contract)) {
      if (contract.contractSide > 0) {
        // 多少 btc, size 张 * contractSize(1张多少btc)
        return numUtil.floor(Big(size).mul(contract.contractSize), 8);
      } else {
        // 多少 btc, size 张 * contractSize(1张多少usd) / price
        if (invalidPrice(price) || Number(price) === 0) {
          return '-';
        }
        /**
         * 价格为 0 的 case 举例：
         * 以市价 + reduceOnly 下的 order，仓位被其他操作平了，这个 order 就会没有任何价格
         */
        return numUtil.floor(Big(size).mul(contract.contractSize).div(price), 8);
      }
    } else {
      return numUtil.floor(size, 8);
    }
  },
  toCoin(contract, size, price) {
    return QtyConvert._display(QtyConvert.toCoinQuantity(contract, size, price), QtyConvert.toCoinUnit(contract));
  },

  toContUnit(contract) {
    return !QtyConvert._isV2(contract) ? 'Cont' : '';
  },
  toContQuantity(contract, size) {
    if (!QtyConvert._isV2(contract)) {
      return size;
    }
    return '-';
  },
  toCont(contract, size) {
    return QtyConvert._display(QtyConvert.toContQuantity(contract, size), QtyConvert.toContUnit(contract));
  },

  _isV2(contract) {
    return contract.type === 'PerpetualV2';
  },
  _display(size, unit) {
    size = size === undefined ? '-' : size;
    return unit ? `${size} ${unit}` : `${size}`;
  },
};

function invalidPrice(price) {
  return isNaN(Number(price)) || price === '';
}

export function calcCurrentQty(contract, size, price, qtyTypeBeforeV2, qtyTypeV2) {
  const qtyType = contract.type === 'Perpetual' ? qtyTypeBeforeV2 : qtyTypeV2;

  const map = {
    [QtyType.Cont]: () => QtyConvert.toCont(contract, size),
    [QtyType.Base]: () => QtyConvert.toValue(contract, size, price),
    [QtyType.Coin]: () => QtyConvert.toCoin(contract, size, price),
  };

  return map[qtyType] && map[qtyType]();
}

export const contractUtil = {
  findContract(contracts, symbol) {
    return contracts.find((contract) => contract.symbol === symbol);
  },
  isPerpetualContract(type) {
    return type === 'Perpetual';
  },
  isPerpetualV2Contract(type) {
    return type === 'PerpetualV2';
  },
  isPilotContract(type) {
    return type === 'PerpetualPilot';
  },
  isNormalContract(type) {
    return ['Perpetual', 'PerpetualV2'].includes(type);
  },
  isContract(type) {
    return ['Perpetual', 'PerpetualV2', 'PerpetualPilot'].includes(type);
  },
  isSupportHedgeMode(type) {
    return this.isPerpetualV2Contract(type) || this.isPilotContract(type);
  },
  filterPilotContract(contracts) {
    return contracts.filter((o) => this.isPilotContract(o.originType));
  },
  filterNormalContract(contracts) {
    return contracts.filter((o) => this.isNormalContract(o.originType));
  },
  getCrossRiskUnitKey(settleCurrency) {
    return settleCurrency + 'Invalid';
  },
  getContractLinkSymbol(contract) {
    return this.isPilotContract(contract.originType) ? contract.symbol.replace(/(.+)(PUSD)/, '$1USDT') : contract.symbol;
  },
  convertPilotContractOriginSymbol(displaySymbol) {
    return (displaySymbol && displaySymbol.replace(/(.+)(USDT)/, '$1PUSD')) || '';
  },
  getContractTypeNum(type) {
    if (this.isPerpetualContract(type)) {
      return 1;
    }
    if (this.isPerpetualV2Contract(type)) {
      return 2;
    }
    if (this.isPilotContract(type)) {
      return 3;
    }
    return 0;
  },
};
